import { AgTechQueryBuilder } from "@agtech/core/data/queries/AgTechDataQueries";
import AgTechGrid from "@agtech/web/components/Grids/AgTechGrid";
import { AgTechGridColumnMetadata } from "@agtech/web/components/Grids/Data/AgTechGridData";
import { AgTechGridSearchOption } from "@agtech/web/components/Grids/Features/AgTechGridSearching";
import { AgTechWarningIcon } from "@agtech/web/components/Icons/AgTechIcons";
import AgTechTooltipContainer from "@agtech/web/components/Portals/Popups/AgTechTooltipContainer";
import { AgTechImportModalResultViewProps, AgTechImportGridItem, isItemImportValid } from "./AgTechImportTypes";
import { gridItems } from "@agtech/web/components/Grids/Data/AgTechGridProps";

export const useGridColumnsForImportResults = <TImportItem, TContext>(
    props: AgTechImportModalResultViewProps<TImportItem, TContext>
) => {
    let importResultsGridColumns = props.importProps.columns.map<AgTechGridColumnMetadata<AgTechImportGridItem<TImportItem>>>(column => ({
        header: column.columnName,
        value: gridItem => {
            let columnValidationErrors = gridItem.importItem.invalidColumns[column.columnName];
            
            if (columnValidationErrors) {
                return (
                    <div className='column-based stretched line-height-inherit invalid px-3 container'>
                        {columnValidationErrors.importedValue}
                        <AgTechTooltipContainer
                            message={columnValidationErrors.errorMessage}
                            classes='ml-auto clickable vertically-centered'
                        >
                            <AgTechWarningIcon classes='ml-auto' />
                        </AgTechTooltipContainer>
                    </div>                        
                )
            }
            else {
                return column.getItemValue(gridItem.importItem.importedItem, props.importData.context);
            }
        },
        itemBasedClasses: gridItem => gridItem.importItem.invalidColumns[column.columnName] ? 'invalid' : '',
        ...(column.getItemGridColumn ? column.getItemGridColumn() : {})
    }));

    importResultsGridColumns = [{
        header: 'Row #',
        value: gridItem => gridItem.importItem.itemRow,
        size: .5,
        classes: 'centered bg-slate-100'
    }, ...importResultsGridColumns];

    return importResultsGridColumns;
}

export const useFailedImportResultsTabs = <TImportItem, TContext>(
    props: AgTechImportModalResultViewProps<TImportItem, TContext>
) => {
    const importResultsGridColumns = useGridColumnsForImportResults(props);

    const successfulImports = props.importResult.items.filter(isItemImportValid);
    const conflictingImports = props.importResult.items.filter(item => item.isConflictingItem);
    const failedImports = props.importResult.items.filter(item => !item.isValidItem);

    const importResultTabs = [];

    const importResultGridSearchOptions = props.importProps.columns.map<AgTechGridSearchOption<AgTechImportGridItem<TImportItem>>>(column => ({
        field: column.columnName,
        value: item => column.getItemValue(item.importItem.importedItem, props.importData.context)
    }));

    if (failedImports.length > 0) {
        importResultTabs.push({
            text: `Failed Imports (${failedImports.length})`,
            content: () => (
                <div className='row-based stretched container'>
                    <AgTechGrid
                        header={{
                            title: `${props.content.importEntityPlural} that failed to import`
                        }}
                        searchConfig={{
                            options: importResultGridSearchOptions
                        }}
                        data={{
                            itemQuery: gridItems(failedImports.map<AgTechImportGridItem<TImportItem>>((item, index) => ({
                                id: index,
                                importItem: {
                                    ...item,
                                    importedItem: { ...item.importedItem, id: index }
                                }
                            })))
                        }}
                        columns={[
                            ...importResultsGridColumns
                        ]}
                        isRowEntityTheSame={() => true}
                    />
                </div>
            )
        });
    }

    if (conflictingImports.length > 0) {
        importResultTabs.push({
            text: `Conflicting Imports (${conflictingImports.length})`,
            content: () => (
                <div className='row-based stretched container'>
                    <AgTechGrid
                        header={{
                            title: `${props.content.importEntityPlural} that already exist`
                        }}
                        searchConfig={{
                            options: importResultGridSearchOptions
                        }}
                        data={{
                            itemQuery: gridItems(conflictingImports.map<AgTechImportGridItem<TImportItem>>((item, index) => ({
                                id: index,
                                importItem: {
                                    ...item,
                                    importedItem: { ...item.importedItem, id: index }
                                }
                            })))
                        }}
                        columns={[
                            ...importResultsGridColumns,
                            {
                                header: 'Message',
                                value: gridItem => gridItem.importItem.importFailureMessage ?? '',
                                width: '360px'
                            }
                        ]}
                        isRowEntityTheSame={() => true}
                    />
                </div>
            )
        });
    }

    importResultTabs.push({
        text: `Successful Imports (${successfulImports.length})`,
        content: () => (
            <div className='row-based stretched container'>
                <AgTechGrid
                    header={{
                        title: `${props.content.importEntityPlural} that imported successfully`
                    }}
                    searchConfig={{
                        options: importResultGridSearchOptions
                    }}
                    data={{
                        itemQuery: gridItems(successfulImports.map<AgTechImportGridItem<TImportItem>>((item, index) => ({
                            id: index,
                            importItem: {
                                ...item,
                                importedItem: { ...item.importedItem, id: index }
                            }
                        })))
                    }}
                    columns={[
                        ...importResultsGridColumns
                    ]}
                    isRowEntityTheSame={() => true}
                />
            </div>
        )
    });

    importResultTabs.push({
        text: `All Imports (${props.importResult.items.length})`,
        content: () => (
            <div className='row-based stretched container'>
                <AgTechGrid
                    header={{
                        title: `All Imported ${props.content.importEntityPlural.toLowerCase()}`
                    }}
                    searchConfig={{
                        options: importResultGridSearchOptions
                    }}
                    data={{
                        itemQuery: gridItems(props.importResult.items.map<AgTechImportGridItem<TImportItem>>((item, index) => ({
                            id: index,
                            importItem: item
                        })))
                    }}
                    columns={[
                        ...importResultsGridColumns,
                        {
                            header: 'Import Status',
                            value: gridItem => isItemImportValid(gridItem.importItem) ? 'Valid' : 'Invalid',
                            classes: 'emphasized centered'
                        }
                    ]}
                    isRowEntityTheSame={() => true}
                />
            </div>
        )
    });

    
    return {
        tabs: importResultTabs
    }
}